import { SuggestionDetails } from "@src/core/engine/technicalSelection/helpers/buildSuggestion";
import { useGetArticleNumbers } from "@src/shared/components/ProductDetailsCard/hooks/useGetProductArticleNumbers";
import { useGetProductDescriptions } from "@src/shared/components/ProductDetailsCard/hooks/useGetProductDescriptions";
import { group, listify, objectify } from "radashi";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useBuildSuggestionOutput } from "./useBuildSuggestionOutput";

export const DESCRIPTION_KEYS_TO_EXCLUDE = [
  "description.dpMax",
  "description.kvs",
  "description.temperatureRange",
];

export function useFormattedSuggestions(suggestions: SuggestionDetails[]) {
  const { t } = useTranslation("product");
  const getArticleNumbers = useGetArticleNumbers();
  const { getProductDescriptions } = useGetProductDescriptions();
  const buildSuggestionOutput = useBuildSuggestionOutput();

  return useMemo(
    () =>
      suggestions.map((item) => ({
        articleNumber: item.articleNumber,
        articleNumbers: objectify(
          getArticleNumbers(item),
          (articleNumber) => articleNumber.label,
          (articleNumber) => articleNumber.value,
        ),
        delimitedId: item.delimitedId,
        description: objectify(
          getProductDescriptions(item).filter(
            ({ translationKey }) =>
              !!translationKey &&
              // Some descriptions are already included in output and complicated
              // to handle on engine side
              !DESCRIPTION_KEYS_TO_EXCLUDE.includes(translationKey),
          ),
          (description) => t(description.translationKey),
          (description) => description.description,
        ),
        id: item.productId,
        isBestChoice: item.isBestChoice,
        name: item.name,
        output: objectify(
          listify(
            group(item.output, (item) => item.translationKey),
            buildSuggestionOutput,
          ),
          (value) => value.translationKey,
          (value) => value,
        ),
        qualifiedId: item.qualifiedId,
        status: item.status,
        subProducts: item.subProducts,
      })),
    [
      suggestions,
      getArticleNumbers,
      getProductDescriptions,
      buildSuggestionOutput,
      t,
    ],
  );
}
